import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useForm from 'react-hook-form';
import PropTypes from 'prop-types';
import api from '../../api';
/* actions */
import { editServices } from '../../redux/actions/services';
import { getAllCategories } from '../../redux/actions/categories';
/* components */
import ErrorMsgField from '../../components/ErrorMsgField';
import AlertMsg from '../../components/AlertMsg';
import FormFooter from '../../components/FormFooter';
import FormHeader from '../../components/FormHeader';
import MultiSelect from '../../components/MultiSelect';
/* form-validation */
import schema from '../../form-validation/service';
/* utils */
import sortable from '../../utils/sortable';
import {
  handleMultiSelect,
  handleMultiDeselect
} from '../../utils/multiSelect';
import handleChangeDefault from '../../utils/handleChangeDefault';
import handleChangeCurrency from '../../utils/handleChangeCurrency';

function Edit (props) {
  const { id } = props.match.params;
  const INITIAL_VALUES = {
    _id: id || '',
    code: '',
    name: '',
    saleValue: '',
    maximumDiscount: '',
    description: '',
    categories: [],
    company: ''
  };
  const dispatch = useDispatch();
  const submitButton = useRef();
  const { register, handleSubmit, errors } = useForm({
    validationSchema: schema,
    reValidateMode: 'onSubmit'
  });
  const services = useSelector(state => state.services);
  const sessionUser = useSelector(state => state.authentications.user);
  const company = sessionUser.company._id;
  const categories = useSelector(state => state.categories.data);
  const [categoriesOptions, setCategoriesOptions] = useState({
    categories: []
  });
  const [ctgrs, setCtgrs] = useState([]);
  const [data, setData] = useState(INITIAL_VALUES);

  useEffect(() => {
    dispatch(getAllCategories({ company }));
  }, [dispatch]);

  useEffect(() => {
    if (categories.length) {
      const ctgrs = categories
        .map(category => ({
          sortValue: category.name,
          value: category._id,
          text: category.name
        }))
        .sort(sortable);
      setCtgrs([...ctgrs]);
    }
  }, [categories, setCtgrs]);

  useEffect(() => {
    (async function () {
      try {
        const params = { _id: id };
        const { data } = await api().get('/api/services', { params });
        if (data[0]) {
          const newData = {
            ...INITIAL_VALUES,
            ...data[0],
            saleValue: String(data[0].saleValue.toFixed(2)).replace(/\./g, ','),
            maximumDiscount: String(data[0].maximumDiscount.toFixed(2)).replace(
              /\./g,
              ','
            )
          };
          setData(newData);
        }
      } catch (err) {
        console.error('Error: ', err);
      }
    })();
  }, []);

  useEffect(() => {
    if (data) {
      const categories = ctgrs.filter(category =>
        data.categories.includes(category.value)
      );
      setCategoriesOptions({ categories });
    }
  }, [data.categories, ctgrs, setCategoriesOptions]);

  const submit = () => {
    const { saleValue, maximumDiscount } = data;
    const newData = {
      ...data,
      saleValue: parseFloat(saleValue.replace(/,/g, '.')),
      maximumDiscount: parseFloat(maximumDiscount.replace(/,/g, '.'))
    };
    dispatch(editServices(newData, submitButton.current));
  };

  return (
    <div className="container-fluid">
      {services.editSuccess && (
        <AlertMsg type="success" msg="Serviço atualizado com sucesso" />
      )}
      {services.errorContent &&
        !services.errorContent.message.includes('400') &&
        services.editError && (
        <AlertMsg type="danger" msg="Falha durante atualização do serviço" />
      )}
      {services.errorContent &&
        services.errorContent.message.includes('400') &&
        services.editError && (
        <AlertMsg
          type="danger"
          msg="Um serviço com o nome informado já está cadastrado"
        />
      )}
      {!services.editSuccess && !services.editError && (
        <AlertMsg type="info" msg="Área atualização de serviços" />
      )}
      <FormHeader title="Atualizar Serviço" />
      <form onSubmit={handleSubmit(submit)}>
        <div className="row">
          <div className="col-sm-6">
            <div className="form-group">
              <label htmlFor="code">
                Código<span className="field-required">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="code"
                name="code"
                autoComplete="off"
                disabled={true}
                value={data.code}
                ref={register}
              />
              {errors.code && errors.code.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
          <div className="col-sm-6">
            <div className="form-group">
              <label htmlFor="name">
                Nome<span className="field-required">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="name"
                name="name"
                autoComplete="off"
                value={data.name}
                onChange={evt => handleChangeDefault(evt, data, setData)}
                ref={register}
              />
              {errors.name && errors.name.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-6">
            <div className="form-group">
              <label htmlFor="saleValue">
                R$ Valor de Venda<span className="field-required">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="saleValue"
                name="saleValue"
                number="on"
                value={data.saleValue}
                autoComplete="off"
                onChange={evt => handleChangeCurrency(evt, data, setData)}
                ref={register}
              />
              {errors.saleValue && errors.saleValue.type === 'currency' && (
                <ErrorMsgField type="currency" />
              )}
              {errors.saleValue && errors.saleValue.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
              {errors.saleValue && errors.saleValue.type === 'min' && (
                <ErrorMsgField type="min" />
              )}
            </div>
          </div>
          <div className="col-sm-6">
            <div className="form-group">
              <label htmlFor="maximumDiscount">
                % Desconto Máx.<span className="field-required">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="maximumDiscount"
                name="maximumDiscount"
                value={data.maximumDiscount}
                autoComplete="off"
                onChange={evt => handleChangeCurrency(evt, data, setData)}
                ref={register}
              />
              {errors.maximumDiscount &&
                errors.maximumDiscount.type === 'currency' && (
                <ErrorMsgField type="currency" />
              )}
              {errors.maximumDiscount &&
                errors.maximumDiscount.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="description">
            Descrição<span className="field-required">*</span>
          </label>
          <textarea
            className="form-control"
            id="description"
            name="description"
            rows="3"
            onChange={evt => handleChangeDefault(evt, data, setData)}
            value={data.description}
            ref={register}
          ></textarea>
          {errors.description && errors.description.type === 'required' && (
            <ErrorMsgField type="required" />
          )}
        </div>
        <MultiSelect
          avaliableOptions={ctgrs}
          selectedOptions={categoriesOptions.categories}
          handleMultiDeselect={options =>
            handleMultiDeselect(
              options,
              'categories',
              categoriesOptions,
              setCategoriesOptions,
              data,
              setData
            )
          }
          handleMultiSelect={options =>
            handleMultiSelect(
              options,
              'categories',
              categoriesOptions,
              setCategoriesOptions,
              data,
              setData
            )
          }
          valueProp="value"
          textProp="text"
          title="Categorias"
          fieldRequired={true}
          name="categories"
          ref={register}
          error={!!errors.categories}
        />
        <FormFooter url="/services" reference={submitButton} />
      </form>
    </div>
  );
}

Edit.propTypes = {
  match: PropTypes.object
};

export default Edit;
